import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

const mastercardLogo = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/izsbzvny_MASTERCARD%20IMAGE.jpg';
const verifiedByVisaLogo = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/r4wb4jhs_VERIFIED%20BY%20VISA%20IMAGE.jpg';
const heroImage = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/t95pmxdt_6.png';

const faqs = [
  {
    q: 'What Is an Acquiring Bank?',
    a: (
      <p>Acquiring Banks are registered members of the card schemes, Mastercard & Visa, and accept (or acquire) transactions on these debit and credit card networks on behalf of a merchant. The card schemes connect acquiring banks to issuing banks (banks that issue credit cards), so that a customer transaction can be verified.</p>
    ),
  },
  {
    q: 'What Is 3D Secure?',
    a: (
      <div className="space-y-3">
        <p>3D Secure is a tool developed by Visa (Verified by Visa) and Mastercard (ID Check) to provide ecommerce merchants with greater security for their payment card transactions when the cardholder is not present in person. With 3D Secure, the card issuer authenticates the cardholder online and thus protects Internet retailers against fraud-related chargebacks.</p>
        <p>Where fraud occurs, if an Internet shop is registered for 3D Secure at the time of the transaction, the card issuer is liable for the fraud and not the merchant.</p>
        <p>3D Secure is launched through your website and interacts with both the cardholder and their card issuer. When your customers are checking out, a window appears asking them to enter a unique, personal code that has been registered with their bank or card issuer. The bank then authenticates the cardholder and provides the shop with evidence of the online purchase.</p>
      </div>
    ),
  },
  {
    q: 'How Is 3D Secure Verified?',
    a: (
      <div className="space-y-4">
        <p>3D Secure is verified by Visa as 'Verified by Visa' and Mastercard as 'Mastercard ID Check'. When a payment request arrives at the merchant or payment gateway, the Merchant Plug In (MPI) component is activated. The MPI talks to Visa or Mastercard to check if the card is enrolled for 3D Secure. If the card is not enrolled, this means that either the bank that issued the card is not yet supporting 3D Secure or it means that the card holder has not yet been registered for the service.</p>
        <div className="flex items-center gap-10 py-4">
          <img src={mastercardLogo} alt="Mastercard ID Check" className="h-44 object-contain" />
          <img src={verifiedByVisaLogo} alt="Verified by Visa" className="h-44 object-contain" />
        </div>
        <p>If the card is enrolled, the MPI will redirect the card holder to the 3D Secure authentication web page for the issuing bank; the card holder will then identify himself. The MPI will evaluate the reply from the bank and, if successful, allow the transaction to proceed for authorization. The transaction could still fail for lack of funds or other reasons but is more likely to be approved because of the authentication.</p>
      </div>
    ),
  },
  {
    q: 'What Does 3D Secure Stand For?',
    a: (
      <div className="space-y-2">
        <p>3D Secure stands for 'Three Domain Model'. The three main domains are:</p>
        <ol className="list-decimal pl-6 space-y-1">
          <li><strong>Acquirer Domain</strong> – the merchant and the bank to which money is being paid.</li>
          <li><strong>Issuer Domain</strong> – the bank which issued the card being used.</li>
          <li><strong>Interoperability Domain</strong> – the infrastructure provided by the card scheme to support the 3-D Secure protocol. The Interoperability Domain includes the Internet, Access Control Servers, Merchant Plug-in providers and other software providers.</li>
        </ol>
      </div>
    ),
  },
  {
    q: 'What Is PCI DSS?',
    a: (
      <p>PCI DSS stands for Payment Card Industry Data Security Standard. It is a set of standards for the payment card industry that is designed to protect cardholder data.</p>
    ),
  },
  {
    q: 'Which Entity Does PCI DSS Apply To?',
    a: (
      <p>It applies to all entities involved in payment card processing including merchants, processors, acquirers, issuers, and service providers, as well as all other entities that store, process or transmit cardholder data.</p>
    ),
  },
  {
    q: 'What Is a Chargeback?',
    a: (
      <p>A chargeback occurs when a credit cardholder contacts their credit card-issuing bank to initiate a refund for a purchase made on their credit card. Chargebacks arise for a variety of reasons but generally they are the result of a cardholder changing their mind, being dissatisfied with their purchase or a case of fraud. The fraud can result from the unauthorised use of their credit card (stolen card) or the cardholder purposely seeking to dispute a legitimate purchase they made ("friendly fraud").</p>
    ),
  },
  {
    q: 'Why Are Chargebacks Bad?',
    a: (
      <div className="space-y-3">
        <p>Visa and Mastercard have strict guidelines for the acceptable number of chargebacks a merchant can receive. If an account is not properly monitored and chargebacks exceed the schemes predetermined threshold(s), the consequences and penalties can be severe.</p>
        <p>If the chargeback rate continues to breach these thresholds, you run the risk of having your merchant agreement terminated and being placed on a Terminated Merchant File with the card schemes. This can hamper your ability to secure the services of another Acquirer.</p>
      </div>
    ),
  },
  {
    q: 'Can I Prevent Chargebacks?',
    a: (
      <div className="space-y-2">
        <p>Understanding the reasons why your customer is disputing a transaction can help identify the main cause and provide you with valuable information on where you need to make changes or consider adopting a new approach within your business. Possible reasons for chargebacks include:</p>
        <ol className="list-decimal pl-6 space-y-1">
          <li>Customer did not receive a product or service</li>
          <li>Customer does not recognise the charge or business name on their card statement</li>
          <li>Customer believes the product or service was defective, damaged or not as it was described</li>
          <li>Customer was a victim of fraud; card was stolen or used without consent</li>
          <li>Customer refund was not processed in a timely manner</li>
        </ol>
      </div>
    ),
  },
  {
    q: 'What Is the Chargeback Process?',
    a: (
      <div className="space-y-3">
        <p>The chargeback process involves many parties: the customer, the issuing bank, the payment network (for example, Visa or Mastercard), the acquirer/processor (such as eCOMM Merchant Solutions) and you, the merchant.</p>
        <p>If a customer disputes a transaction, the issuing bank will credit their customer for the amount in dispute and the chargeback process begins. The disputed amount is debited from your merchant account as part of the process.</p>
        <p>If you provide documentary evidence and successfully challenge the chargeback in the necessary timeframe, the funds will be returned to you. If you do not submit documentation in time, or you are unsuccessful in challenging the chargeback, the funds will not be returned to you.</p>
      </div>
    ),
  },
  {
    q: 'Why Do Customers Dispute Transactions?',
    a: (
      <p>One of the main reasons behind a chargeback is poor customer service. If a customer purchased a product from you and for some reason decides they want a refund, they might request a chargeback. If a customer cannot directly contact a Merchant, they will normally file a chargeback – for example if you do not process refunds in a timely manner.</p>
    ),
  },
  {
    q: 'How Does Fraud Affect Chargebacks?',
    a: (
      <div className="space-y-3">
        <p>Fraud is one of the most common reasons for chargebacks. This is typically where the customers' financial data has been stolen, and this information is used to make a purchase. The customer did not authorise this purchase and disputes the transaction by claiming fraud has occurred.</p>
        <p>
          See eCOMM's Tips on{' '}
          <Link to="/fraud-prevention" className="text-[#1F4E79] font-semibold underline hover:text-[#163a5f] transition-colors duration-200">
            Fraud Prevention
          </Link>.
        </p>
      </div>
    ),
  },
  {
    q: 'What Is Friendly Fraud?',
    a: (
      <p>This is where the actual cardholder has authorised the transaction, but, after receiving the goods or services, tries to get his or her money back by calling the credit card issuer to claim that the transactions were not authorised, or that the products were never received. Just like in other fraud scenarios described above, if you, as a merchant, cannot prove that the cardholder received the goods or services, then you will likely lose the dispute and end up paying for the chargeback.</p>
    ),
  },
  {
    q: 'How Can You Detect Fraudulent Transactions?',
    a: (
      <div className="space-y-3">
        <p>The Card Schemes have a list of tips published on their websites to help merchants identify potentially fraudulent transactions and reduce the risk of becoming a fraud target. According to the list, there are 12 fraud indicators that you should keep your eyes open for. If more than one of the following indicators are present, fraud may be involved:</p>
        <ol className="list-decimal pl-6 space-y-2">
          <li><strong>First-time shopper:</strong> Criminals are always looking for new merchants to defraud</li>
          <li><strong>Larger-than-normal orders:</strong> Because stolen cards or account numbers have a limited life span, criminals need to maximize the size of their purchase</li>
          <li><strong>Orders that include several varieties of the same item:</strong> Having multiples of the same item increases a criminal's profits</li>
          <li><strong>Orders made up of "big-ticket" items:</strong> These items have maximum profit potential</li>
          <li><strong>"Rush" or "overnight" shipping:</strong> Criminals want their fraudulently obtained items as soon as possible for the quickest possible resale and aren't concerned about extra delivery charges</li>
          <li><strong>Shipping to an international address:</strong> A significant number of fraudulent transactions are shipped to fraudulent cardholders outside of Canada/U.S</li>
          <li><strong>Transactions made with similar account numbers:</strong> May indicate the account numbers used have been generated using software available on the Internet</li>
          <li><strong>Shipping to a single address, but transactions placed on multiple cards:</strong> Could involve an account number generated using special software, or even a batch of stolen cards</li>
          <li><strong>Multiple transactions on one card over a very short period of time:</strong> Could be an attempt to "run a card" until the account is closed</li>
          <li><strong>Multiple transactions on one card or a similar card with a single billing address, but multiple shipping addresses:</strong> Could represent organized activity, rather than one individual at work</li>
          <li><strong>In online transactions, multiple cards used from a single IP address:</strong> More than one or two cards could indicate a fraud scheme</li>
          <li><strong>Orders from Internet addresses that make use of free e-mail services:</strong> These e-mail services involve no billing relationships, and often neither an audit trail nor verification that a legitimate cardholder has opened the account</li>
        </ol>
        <p>These are good indicators of fraud with the exception of number 12. Free email addresses from sites such as Gmail, Hotmail, Yahoo, Live, etc. are now extremely common, as many people do not want to be tied to the email address supplied by their ISP. For that reason, merchants should not worry about a transaction where the customer uses a 'free' email address.</p>
        <p>Another common fraud indicator to add to the list is when the customer has input information which is incomplete, uses a fictitious/famous name, the use of capital letters or the use of "C/O" or "PO Box" addresses on the order form. This often coincides with a shopper IP address that does not match the country where the credit card was issued.</p>
      </div>
    ),
  },
];

const AcceptingPaymentsFAQs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromTopNav = location.state?.from === 'top-nav';

  return (
    <div className="min-h-screen flex flex-col" data-testid="accepting-payments-faqs-page">
      <Header />

      <div className="h-20" />

      {/* Full-page background image */}
      <div className="relative flex-1">
        <div
          className="fixed inset-0 top-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="fixed inset-0 top-20 bg-white/50" />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
          <button
            onClick={() => (fromTopNav ? navigate('/') : navigate(-1))}
            className="flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-medium mb-8 transition-colors duration-200"
            data-testid="faqs-back-button"
          >
            <ArrowLeft size={18} />
            {fromTopNav ? 'Back to Home' : 'Back'}
          </button>

          {/* Intro */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3" data-testid="faqs-page-title">
              Accepting Payments FAQs
            </h1>
            <p className="text-gray-600 text-lg font-bold mb-6">
              Everything you need to know about payment processing, security and chargebacks.
            </p>
            <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
              <p>
                Payment processing can seem complex, but it doesn't have to be. Our Accepting Payments FAQs provide clear, straightforward answers to the questions we hear most often from merchants like you. Whether you're new to payment processing or looking to understand specific features, you'll find the information you need right here.
              </p>
              <p>
                Browse through the questions below to find specific topics. If you can't find the answer you're looking for, our support team is always ready to help with personalised assistance.
              </p>
            </div>
          </div>

          {/* FAQ Accordion */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-10">
            <Accordion type="single" collapsible className="w-full" data-testid="faqs-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`faq-${index}`} className="border-b border-gray-200">
                  <AccordionTrigger
                    className="text-left text-base font-semibold text-gray-900 hover:text-[#1F4E79] hover:no-underline py-5"
                    data-testid={`faq-trigger-${index}`}
                  >
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed" data-testid={`faq-content-${index}`}>
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>
      <BackToTop />
    </div>
  );
};

export default AcceptingPaymentsFAQs;
